import { z } from "zod";

export enum AudioCaptureStatus {
  Complete = "complete",
  Partial = "partial",
  Unavailable = "unavailable",
}

export enum AudioMeasurementStage {
  BeforeAgentNoiseCancellation = "before_agent_noise_cancellation",
}

export const CALLER_AUDIO_SAMPLE_RATE = 16_000;
export const AUDIO_LEVEL_FLOOR_DBFS = -96;
export const AUDIO_RMS_WINDOW_MS = 100;
export const AUDIO_FRAME_GAP_THRESHOLD_MS = 200;
export const AUDIO_NEAR_FULL_SCALE_THRESHOLD = 0.99;

const durationSchema = z.number().nonnegative();
const levelSchema = z.number().min(AUDIO_LEVEL_FLOOR_DBFS).max(0);

/** Signal measurements only. Quiet windows include listening time; arrival gaps are not packet-loss counts. */
export const audioDiagnosticsSchema = z.object({
  version: z.literal(1),
  stage: z.enum(AudioMeasurementStage),
  status: z.enum(AudioCaptureStatus),
  coverage: z
    .object({
      startedAtMs: z.number().nonnegative(),
      elapsedMs: durationSchema,
      observedMs: durationSchema,
      frameCount: z.number().int().nonnegative(),
      firstFrameDelayMs: durationSchema.nullable(),
      lastFrameAgeMs: durationSchema.nullable(),
      frameGapThresholdMs: z.literal(AUDIO_FRAME_GAP_THRESHOLD_MS),
      frameGapCount: z.number().int().nonnegative(),
      frameGapTotalMs: durationSchema,
      longestFrameGapMs: durationSchema,
    })
    .nullable(),
  signal: z
    .object({
      sampleRate: z.literal(CALLER_AUDIO_SAMPLE_RATE),
      windowMs: z.literal(AUDIO_RMS_WINDOW_MS),
      levelFloorDbfs: z.literal(AUDIO_LEVEL_FLOOR_DBFS),
      percentileResolutionDb: z.literal(1),
      rmsDbfs: levelSchema,
      p50WindowRmsDbfs: levelSchema,
      p95WindowRmsDbfs: levelSchema,
      peakDbfs: levelSchema,
      strongest1sRmsDbfs: levelSchema.nullable(),
      nearFullScaleThreshold: z.literal(AUDIO_NEAR_FULL_SCALE_THRESHOLD),
      nearFullScaleFraction: z.number().min(0).max(1),
      quietWindows: z
        .array(
          z.object({
            thresholdDbfs: levelSchema,
            totalMs: durationSchema,
            longestMs: durationSchema,
          })
        )
        .max(2),
    })
    .nullable(),
});

export type AudioDiagnostics = z.infer<typeof audioDiagnosticsSchema>;

export function unavailableAudioDiagnostics(): AudioDiagnostics {
  return {
    version: 1,
    stage: AudioMeasurementStage.BeforeAgentNoiseCancellation,
    status: AudioCaptureStatus.Unavailable,
    coverage: null,
    signal: null,
  };
}
